import {styleMap} from '@script/colorz/configMap';
import {
	MarkerClusterer,
	SuperClusterAlgorithm,
	GridAlgorithm,
} from '@googlemaps/markerclusterer';
import emitter from '@utils/Emitter.js';

export default class StoreLocator extends HTMLElement {
	constructor() {
		super();
		this.el = this;
		this.elStoreSearch = this.querySelector('[data-storloc-search]');
		this.elStoreResult = this.querySelector('[data-storloc-result]');
		this.elStoreResultList = this.querySelector('[store-locator-result--list]');
		this.elStoreMap = this.querySelector('[data-storloc-map]');
		this.filters = this.querySelectorAll('[data-filter]');

		this.elForm = this.elStoreSearch.querySelector('form');

		this.map = [];
		this.markers = [];
		this.styleMap = styleMap;
		this.geocoder = [];
		this.clusters = null;
		this.resetMarkers = true;
		this.center = {
			lat: 23.6345,
			lng: -102.5528,
		};

		this.stores = window.storelocator.stores;
		this.mapContainer = this.el.querySelector('[data-google-map]');
		this.listStores = this.el.querySelectorAll('[data-store]');

		this.submitButton = this.el.querySelector('[data-search-btn]');
		this.inputText = this.el.querySelector('[data-input-text]');
		this.selectRadius = this.el.querySelector('[data-select-radius]');
		this.distanceError = this.el.querySelector('[data-distance-error]');
		this.localize = this.el.querySelector('[data-localize]');
		this.isList = this.el.querySelector('[data-list]');
		this.isMap = this.el.querySelector('[data-map]');
		this.storesContainer = this.el.querySelector('[data-store-list]');

		this.filterCheckboxes = {};

		/** Dernier point de recherche utilisé pour les requêtes API (filtres inclus). */
		this.lastSearchLat = null;
		this.lastSearchLng = null;
		/** Dernier résultat géocode (formulaire) pour le mode distance « sans limite » + refiltre. */
		this.lastGeocodeResults = null;
		this.hasSearched = false;

		this.filters.forEach((filter) => {
			const id = filter.getAttribute('id');
			if (id) {
				this.filterCheckboxes[id] = filter;
			}
		});
	}

	/**
	 * Libellés `type` à envoyer au backend (types), depuis les cases cochées.
	 * @returns {string[]}
	 */
	getSelectedFilterTypes() {
		const types = [];
		for (const el of Object.values(this.filterCheckboxes)) {
			if (el.checked && el.dataset.filterType) {
				types.push(el.dataset.filterType);
			}
		}
		return types;
	}

	/**
	 * Applique latitude/longitude/radius et types à l’URL storelocator-v2.
	 * @param {URL} url
	 * @param {string|number} lat
	 * @param {string|number} lng
	 * @param {string} radius Valeur du select (ex. "10", "999999", "0")
	 */
	appendStoreLocatorQuery(url, lat, lng, radius) {
		url.searchParams.set('latitude', String(lat));
		url.searchParams.set('longitude', String(lng));

		if (radius && radius !== '0' && radius !== '999999') {
			url.searchParams.set('radius', String(radius));
		}

		const selectedTypes = this.getSelectedFilterTypes();
		// Plusieurs clés `types=…&types=…` → 500 sur certains backends : une seule clé.
		// Plusieurs libellés : `types=Lunette,Parapharmacie` ; l’OR reste assuré par filterStoresBySelectedTypes.
		if (selectedTypes.length === 1) {
			url.searchParams.set('types', selectedTypes[0]);
		} else if (selectedTypes.length > 1) {
			url.searchParams.set('types', selectedTypes.join(','));
		}
	}

	/**
	 * Si des filtres sont cochés : exclut les magasins sans `type` et ceux qui ne
	 * correspondent à aucun libellé sélectionné (même logique substring que l’API).
	 * @param {unknown[]} stores
	 * @returns {unknown[]}
	 */
	filterStoresBySelectedTypes(stores) {
		const selected = this.getSelectedFilterTypes();
		if (!Array.isArray(stores)) {
			return stores;
		}
		if (selected.length === 0) {
			return stores;
		}
		return stores.filter((s) => {
			if (!s) {
				return false;
			}
			const raw = s.type;
			if (raw == null || String(raw).trim() === '') {
				return false;
			}
			const haystack = String(raw).toLowerCase();
			return selected.some((needle) =>
				haystack.includes(String(needle).toLowerCase()),
			);
		});
	}

	/**
	 * Résout lat/lng pour une requête (filtre sans recherche préalable : centre de carte ou défaut).
	 * @returns {{ lat: number, lng: number }}
	 */
	resolveSearchCenter() {
		if (
			this.lastSearchLat != null &&
			this.lastSearchLng != null &&
			!Number.isNaN(this.lastSearchLat) &&
			!Number.isNaN(this.lastSearchLng)
		) {
			return {lat: this.lastSearchLat, lng: this.lastSearchLng};
		}
		if (this.map && typeof this.map.getCenter === 'function') {
			const c = this.map.getCenter();
			return {lat: c.lat(), lng: c.lng()};
		}
		return {lat: this.center.lat, lng: this.center.lng};
	}

	initMap() {
		this.map = new google.maps.Map(this.elStoreMap, {
			center: this.center,
			zoom: 10,
			mapTypeId: 'roadmap',
			styles: this.styleMap,
			mapTypeControl: false,
		});

		this.geocoder = new google.maps.Geocoder();
		this.infoWindow = new google.maps.InfoWindow();
		this.clearLocations();
		this.showStoresMarkers(this.stores);
		this.setOnClickListener();
	}

	connectedCallback() {
		this.elForm.addEventListener('submit', (e) => {
			this.getStoresList(e);
		});

		this.initMap();

		google.maps.event.addListenerOnce(this.map, 'idle', () =>
			//Quand la map est correctement initialiser, on set les clusters
			this.showVisibleMarkers(this.map, this.markers, this.listStores),
		);

		this.inputText.addEventListener('input', () => {
			if (this.inputText.value.trim() === '') {
				this.submitButton.setAttribute('disabled', 'true');
			} else {
				this.submitButton.removeAttribute('disabled');
			}
		});

		this.selectRadius.addEventListener('change', () => {
			if (!this.isRadiusPlaceholder(this.selectRadius.value)) {
				this.clearDistanceError();
			}
		});

		this.inputText.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				this.elForm.requestSubmit();
			}
		});

		if (this.localize) {
			this.localize.addEventListener('click', () => {
				this.localizeUser(this.map);
			});
		}

		Object.values(this.filterCheckboxes).forEach((checkbox) => {
			checkbox.addEventListener('change', () => {
				this.refetchStoresForFilters();
			});
		});

		if (window.innerWidth < 1080) {
			this.elStoreMap.classList.add('is-mobile');
			this.elStoreResult.classList.add('is-mobile');
			this.switchDisplay();
			this.displayList();
		} else {
			this.elStoreMap.classList.remove('is-mobile');
			this.elStoreResult.classList.remove('is-mobile');
		}

		this.previousViewportIsMobile = window.innerWidth < 1080;

		this.handleResize = () => {
			const isMobile = window.innerWidth < 1080;
			if (isMobile !== this.previousViewportIsMobile) {
				location.reload();
			}
			this.previousViewportIsMobile = isMobile;
		};

		window.addEventListener('resize', this.handleResize);
	}

	/** Rayon non choisi (placeholder du select). */
	isRadiusPlaceholder(radius) {
		return !radius || radius === '0';
	}

	/** Retourne le rayon choisi dans le formulaire. */
	resolveRadiusForSearch() {
		return this.selectRadius ? this.selectRadius.value : '0';
	}

	showDistanceError() {
		if (this.distanceError) {
			this.distanceError.hidden = false;
		}
		this.selectRadius?.setAttribute('aria-invalid', 'true');
		this.selectRadius?.focus();
	}

	clearDistanceError() {
		if (this.distanceError) {
			this.distanceError.hidden = true;
		}
		this.selectRadius?.removeAttribute('aria-invalid');
	}

	/**
	 * Géocode une adresse, une ville ou un code postal dans le pays du marché
	 * courant, puis retente sans restriction pour les recherches transfrontalières.
	 */
	async geocodeSearch(query) {
		const address = query.trim();
		const countryCode = window.storelocator?.countryCode;

		if (countryCode) {
			try {
				const restrictedResponse = await this.geocoder.geocode({
					address,
					componentRestrictions: {country: countryCode},
				});
				if (restrictedResponse.results?.length) {
					return restrictedResponse.results;
				}
			} catch (error) {
				if (error !== 'ZERO_RESULTS') {
					console.warn('Country-restricted geocode failed:', error);
				}
			}
		}

		const response = await this.geocoder.geocode({address});
		return response.results || [];
	}

	/**
	 * Liste + marqueurs comme au chargement (métaobjets Shopify), sans appel API.
	 */
	restoreOriginalCatalog() {
		const stores = this.stores || [];
		const data = {
			stores: stores.map((st) => ({
				display_name: st.name,
				name: st.name,
				street: st.address?.street ?? '',
				zip_code: st.address?.zip_code ?? '',
				city: st.address?.city ?? '',
				country: st.address?.country ?? '',
			})),
		};
		this.clearLocations();
		this.updateList(data);
		this.showStoresMarkers(stores, false);
		this.setOnClickListener();
		this.showVisibleMarkers(this.map, this.markers, this.listStores);
		google.maps.event.addListenerOnce(this.map, 'idle', () => {
			this.showVisibleMarkers(this.map, this.markers, this.listStores);
		});
	}

	/**
	 * Liste vide + compteur « aucune boutique » (ex. erreur serveur sur les filtres).
	 */
	applyEmptyStoreResults() {
		const empty = {stores: []};
		this.clearLocations();
		this.updateList(empty);
		this.showStoresMarkers([], true);
		this.setOnClickListener(true, []);
		this.showVisibleMarkers(this.map, this.markers, this.listStores);
	}

	/**
	 * Recharge les magasins depuis l’API avec les filtres courants (types).
	 * Utilise le dernier point de recherche, ou le centre de la carte par défaut.
	 */
	async refetchStoresForFilters() {
		const radius = this.selectRadius ? this.selectRadius.value : '0';
		if (
			this.getSelectedFilterTypes().length === 0 &&
			this.isRadiusPlaceholder(radius)
		) {
			this.restoreOriginalCatalog();
			return;
		}
		const {lat, lng} = this.resolveSearchCenter();
		await this.fetchAndApplyStoreResults(lat, lng, radius, {
			geocodeResults: this.lastGeocodeResults,
			fromFilterCheckbox: true,
		});
	}

	/**
	 * Appelle storelocator-v2 et met à jour liste + carte.
	 *
	 * @param {number} lat
	 * @param {number} lng
	 * @param {string} radius
	 * @param {object} [options]
	 * @param {google.maps.GeocoderResult | null} [options.centerOnGeocode] — recentrer la carte après une recherche adresse
	 * @param {google.maps.GeocoderResult[]} [options.geocodeResults] — pour le mode sans limite (tri distance)
	 * @param {boolean} [options.fromFilterCheckbox] — si true, erreur serveur → message « aucune boutique »
	 */
	async fetchAndApplyStoreResults(lat, lng, radius, options = {}) {
		const {
			centerOnGeocode = null,
			geocodeResults = null,
			fromFilterCheckbox = false,
		} = options;

		const url = new URL(`${window.shopUrl}/apps/clrz/storelocator-v2`);
		this.appendStoreLocatorQuery(url, lat, lng, radius);

		try {
			const response = await fetch(url);
			if (response.status >= 500) {
				if (fromFilterCheckbox) {
					this.applyEmptyStoreResults();
				} else {
					console.error('Store locator fetch error:', response.status);
				}
				return;
			}
			if (!response.ok) {
				console.error('Store locator fetch failed:', response.status);
				return;
			}

			let data;
			try {
				data = await response.json();
			} catch (parseErr) {
				console.error('Store locator JSON parse failed:', parseErr);
				if (fromFilterCheckbox) {
					this.applyEmptyStoreResults();
				}
				return;
			}

			const geocodeFirst =
				geocodeResults && geocodeResults[0] ? geocodeResults[0] : null;

			// Sans limite de rayon : tri par distance depuis le point recherché
			if (radius === '999999' && geocodeFirst) {
				const origin = geocodeFirst.geometry.location;
				const originLat = origin.lat();
				const originLng = origin.lng();
				let candidateStores = Array.isArray(data.stores) ? data.stores : [];
				if (!candidateStores.length) {
					candidateStores = (this.stores || []).map((st) => ({
						latitude: st?.coordinates?.latitude,
						longitude: st?.coordinates?.longitude,
						street: st?.address?.street,
						zip_code: st?.address?.zip_code,
						city: st?.address?.city,
						country: st?.address?.country,
						name: st?.name,
						phoneNumber: st?.phone,
						type: st?.type,
					}));
				}
				data.stores = (candidateStores || [])
					.filter(
						(s) =>
							s &&
							s.latitude != null &&
							s.longitude != null &&
							!Number.isNaN(parseFloat(s.latitude)) &&
							!Number.isNaN(parseFloat(s.longitude)),
					)
					.map((s) => {
						const d = this.calculateDistance(
							originLat,
							originLng,
							parseFloat(s.latitude),
							parseFloat(s.longitude),
						);
						return {...s, __distance: d};
					})
					.sort((a, b) => a.__distance - b.__distance)
					.map(({__distance, ...rest}) => rest);
			}

			if (Array.isArray(data.stores)) {
				data.stores = this.filterStoresBySelectedTypes(data.stores);
			}

			this.hasSearched = true;
			this.lastSearchLat = lat;
			this.lastSearchLng = lng;

			this.clearLocations();
			this.updateList(data);
			this.showStoresMarkers(data.stores, true, {
				adjustView: !fromFilterCheckbox,
			});
			this.setOnClickListener(true, data.stores);

			if (fromFilterCheckbox && data.stores?.length > 0) {
				this.applyMapViewAfterFilterRefetch(lat, lng, radius, data.stores);
			}

			if (centerOnGeocode && radius !== '999999') {
				this.map.setCenter(centerOnGeocode.geometry.location);
				if (data.stores && data.stores.length <= 5) {
					const adaptiveZoom = this.calculateZoomFromRadius(radius);
					this.map.setZoom(adaptiveZoom);
				}
			}

			if (geocodeResults && geocodeResults.length) {
				this.lastGeocodeResults = geocodeResults;
			}

			this.showVisibleMarkers(this.map, this.markers, this.listStores);
		} catch (error) {
			console.error('Error fetching stores:', error);
		}
	}

	/**
	 * Handles the store locator form submission, geocodes the input address,
	 * constructs a request URL with the geocoded coordinates and selected radius,
	 * fetches the list of stores from the server, and updates the UI with the results.
	 *
	 * @async
	 * @param {Event} e - The form submission event.
	 * @returns {Promise<void>}
	 */
	async getStoresList(e) {
		e.preventDefault();
		const radius = this.resolveRadiusForSearch();

		if (this.isRadiusPlaceholder(radius)) {
			this.showDistanceError();
			return;
		}
		this.clearDistanceError();

		let results;
		try {
			results = await this.geocodeSearch(this.inputText.value);
		} catch (err) {
			console.error('Geocode failed:', err);
			results = [];
		}

		if (!results || results.length === 0) {
			return;
		}

		const lat = results[0].geometry.location.lat();
		const lng = results[0].geometry.location.lng();

		await this.fetchAndApplyStoreResults(lat, lng, radius, {
			centerOnGeocode: results[0],
			geocodeResults: results,
		});
	}

	updateList(data) {
		this.elStoreResultList.innerHTML = '';

		this.updateCount(data.stores.length);
		data.stores.forEach((store, i) => {
			const div = document.createElement('div');
			div.className =
				'u-flex u-py-20 u-px-5 u-gap-x-20 u-border-b u-border-solid u-border-grey';
			if (i == 0) {
				div.className =
					'u-flex u-py-20 u-px-5 u-gap-x-20 u-border-b u-border-solid u-border-grey u-border-t';
			}
			div.style.display = 'grid';
			div.setAttribute('data-store', '');
			div.innerHTML = `
		<p class="store-locator-result__address t-text u-text-blue">
			<span class="t-small-title t-small-title--12">
			${store.display_name ? store.display_name : store.name}
			</span>
			<br>
			${store.street}
			<br>
			${store.zip_code}

			${store.city}
			<br>
			${store.country ? store.country : ''}
		</p>
		`;

			this.elStoreResultList.appendChild(div);
		});

		// 🔥 Add this line to ensure new DOM elements are used
		this.listStores = this.el.querySelectorAll('[data-store]');
	}

	geocode(request) {
		this.geocoder
			.geocode(request)
			.then((result) => {
				const {results} = result;
				this.map.setCenter(results[0].geometry.location);
				this.map.setZoom(10);
				this.showVisibleMarkers(this.map, this.markers, this.listStores);
				return results;
			})
			.catch((e) => {
				this.map.setCenter(this.center);
				this.map.setZoom(5);
				this.showVisibleMarkers(this.map, this.markers, this.listStores);
				console.log(
					'Geocode was not successful for the following reason: ' + e,
				);
			});
	}

	setOnClickListener(fromProxy, listStores) {
		let stores = this.el.querySelectorAll('[data-store]');

		stores.forEach((el, i) => {
			el.addEventListener('click', () => {
				if (fromProxy) {
					var latitude = listStores[i].latitude;
					var longitude = listStores[i].longitude;
				} else {
					var latitude = this.stores[i].coordinates.latitude;
					var longitude = this.stores[i].coordinates.longitude;
				}
				let storeLatlng = {
					lat: parseFloat(latitude),
					lng: parseFloat(longitude),
				};
				this.map.setCenter(storeLatlng);
				this.map.setZoom(16);
				setTimeout(() => {
					google.maps.event.trigger(this.markers[i], 'click');
				}, 500);
				this.displayMap();
			});
			emitter.emit('store:clicked');
		});
	}

	/**
	 * @param {boolean} [options.adjustView=true] — si false, ne change pas centre/zoom (ex. refiltre : vue gérée ailleurs).
	 */
	showStoresMarkers(stores, fromProxy, options = {}) {
		const adjustView = options.adjustView !== false;
		var bounds = new google.maps.LatLngBounds();
		let firstLatLng = null;

		stores.forEach((store) => {
			let type;
			if (fromProxy) {
				var latlng = new google.maps.LatLng(store.latitude, store.longitude);
				var street = store.street;
				var zip = store.zip_code;
				var city = store.city;
				type = store.type;
			} else {
				var latlng = new google.maps.LatLng(
					store.coordinates.latitude,
					store.coordinates.longitude,
				);
				var street = store.address.street;
				var zip = store.address.zip_code;
				var city = store.address.city;
				type = store.type;
			}
			var name = store.display_name || store.name;
			var phone = store.phoneNumber || store.phone;
			if (!firstLatLng) {
				firstLatLng = latlng;
			}
			bounds.extend(latlng);
			this.createMarker(latlng, name, street, city, zip, phone, type);
		});

		if (!adjustView || stores.length === 0) {
			return;
		}

		if (stores.length === 1) {
			this.map.setCenter(firstLatLng);
			this.map.setZoom(14);
		} else if (stores.length > 1) {
			this.map.fitBounds(bounds, 72);
			google.maps.event.addListenerOnce(this.map, 'idle', () => {
				const z = this.map.getZoom();
				if (z > 15) {
					this.map.setZoom(15);
				}
			});
		}
	}

	/**
	 * Vue carte après changement de filtre : évite fitBounds trop serré avec peu de points.
	 * Centre sur le point de recherche + zoom cohérent avec le rayon, ou sur le magasin unique.
	 */
	applyMapViewAfterFilterRefetch(lat, lng, radius, stores) {
		if (!stores?.length) {
			return;
		}

		if (stores.length === 1) {
			const s = stores[0];
			const pos = {
				lat: parseFloat(s.latitude),
				lng: parseFloat(s.longitude),
			};
			if (!Number.isNaN(pos.lat) && !Number.isNaN(pos.lng)) {
				this.map.setCenter(pos);
				this.map.setZoom(14);
			}
			return;
		}

		this.map.setCenter({lat, lng});

		if (radius && radius !== '0' && radius !== '999999') {
			this.map.setZoom(this.calculateZoomFromRadius(radius));
			return;
		}

		if (radius === '999999') {
			this.map.setZoom(9);
			return;
		}

		this.map.setZoom(stores.length <= 5 ? 11 : 10);
	}

	createMarker(latlng, name, street, city, zip, phone, type) {
		//HTML pour l'affichage des infos des stores sur la map
		var html = `
    <div class="u-text-black u-max-w-[250px] u-leading-[18px]">
    <div class="surtitle u-text-[#000] u-font-bold u-mb-5">
      ${name}
    </div> `;

		if (street || zip || city || phone) {
			html += `<div class="u-mt-4 u-mb-8">`;
			if (street) {
				html += `<span class="text u-block">${street}</span>`;
			}
			if (zip || city) {
				html += `<span class="text u-block u-uppercase">${zip ? zip : ''}${
					zip && city ? ' ' : ''
				}${city ? city : ''}</span>`;
			}
			if (phone) {
				html += `<span class="text u-block u-mt-10">${phone}</span>`;
			}
			html += `</div>`;
		}

		html += `
			<div class="u-flex u-justify-between u-mt-10">
				<a class="t-link t-link--underline" href="https://www.google.com/maps/dir/?api=1&destination=${latlng}" target="_blank">${window.storelocator.itinerary}</a>
			</div>
		</div>
    `;

		//Changement de l'icon sur la map
		var mainIcon = {
			url: 'https://cdn.shopify.com/s/files/1/0663/0817/7974/files/map-pin-lightblue.svg?v=1753884480',
			scaledSize: new google.maps.Size(20, 27),
		};

		//Création du marker avec les infos lié.
		const isUnlimited =
			this.selectRadius && this.selectRadius.value === '999999';
		var markerOptions = {
			position: latlng,
			icon: mainIcon,
			type: type,
		};
		if (!isUnlimited) {
			markerOptions.map = this.map;
			markerOptions.visible = true;
		}
		var marker = new google.maps.Marker(markerOptions);
		marker.type = type;

		google.maps.event.addListener(marker, 'click', () => {
			this.infoWindow.setContent(html);
			this.infoWindow.open(this.map, marker);
		});
		this.markers.push(marker);
	}

	showVisibleMarkers(map, markers, stores) {
		emitter.on('store:clicked', () => {
			this.resetMarkers = false;
		});

		if (this.resetMarkers === true) {
			if (this.clusters != null) {
				this.clusters.setMap(null);
			}

			const bounds = map.getBounds();
			if (bounds) {
				const visibleMarkers = [];
				const keepFullList = window.innerWidth < 1080;

				markers.forEach((marker, index) => {
					const store = stores[index];
					const isVisible = bounds.contains(marker.getPosition());

					if (isVisible) {
						if (store instanceof HTMLElement) {
							store.style.display = 'grid';
						}
						// Retirer du map : les pins étaient attachés dans createMarker ; le clusterer
						// doit être le seul à les afficher (sinon marqueurs individuels + pas de clusters).
						marker.setMap(null);
						visibleMarkers.push(marker);
					} else {
						if (store instanceof HTMLElement) {
							store.style.display = keepFullList ? 'grid' : 'none';
						}
						// Retirer du map les markers hors fenêtre/filtre
						marker.setMap(null);
					}
				});

				this.clusters = new MarkerClusterer({
					map,
					markers: visibleMarkers,
					algorithm: new GridAlgorithm({gridSize: 60}),
					renderer: {
						render: ({markers, position}) => {
							return new google.maps.Marker({
								position,
								optimized: true,
								label: {
									text: String(markers.length),
									color: 'white',
								},
								icon: 'https://cdn.shopify.com/s/files/1/0611/6307/7732/files/cluster_01_1.png?v=1712048346',
							});
						},
					},
				});

				this.updateCount(keepFullList ? markers.length : visibleMarkers.length);
			}
		}

		this.resetMarkers = true;
	}

	// Distance en kilomètres (Haversine)
	calculateDistance(lat1, lon1, lat2, lon2) {
		const toRad = (v) => (v * Math.PI) / 180;
		const R = 6371; // km
		const dLat = toRad(lat2 - lat1);
		const dLon = toRad(lon2 - lon1);
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRad(lat1)) *
				Math.cos(toRad(lat2)) *
				Math.sin(dLon / 2) *
				Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	}

	// Calcule le niveau de zoom optimal basé sur la distance de recherche
	calculateZoomFromRadius(radius) {
		if (!radius || radius === '999999' || radius === '0') {
			return 10;
		}

		const distance = parseFloat(radius);
		if (distance <= 5) {
			return 14;
		} else if (distance <= 10) {
			return 13;
		} else if (distance <= 50) {
			return 12;
		} else if (distance <= 75) {
			return 11;
		} else if (distance <= 100) {
			return 10;
		} else if (distance <= 200) {
			return 9;
		} else {
			return 7;
		}
	}

	updateCount(storesLength) {
		const count = storesLength;
		const countEls = this.el.querySelectorAll('[data-count]');
		countEls.forEach((countEl) => {
			if (!this.hasSearched) {
				countEl.innerHTML = window.storelocator.shops_count_hint;
			} else if (count > 1) {
				countEl.innerHTML = `${count} ${window.storelocator.shops}`;
			} else if (count === 1) {
				countEl.innerHTML = `${count} ${window.storelocator.shop}`;
			} else {
				countEl.innerHTML = window.storelocator.no_shop;
			}
		});
	}

	clearLocations() {
		this.infoWindow.close();
		for (var i = 0; i < this.markers.length; i++) {
			this.markers[i].setMap(null);
		}
		this.markers.length = 0;
	}

	//Permet de géocaliser l'utilisateur sur la map
	localizeUser(map) {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const pos = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};
					map.setCenter(pos);
					map.setZoom(12);
					this.showVisibleMarkers(map, this.markers, this.listStores);
				},
				() => {
					alert('Impossible de vous géolocaliser');
				},
			);
		} else {
			// Browser doesn't support Geolocation
			alert("Browser doesn't support Geolocation");
		}
	}

	//Sur mobile, permet de switcher entre la liste de store et la map
	switchDisplay() {
		this.isList.addEventListener('click', () => {
			this.displayList();
		});
		this.isMap.addEventListener('click', () => {
			this.displayMap();
		});
	}

	displayList() {
		if (!this.elStoreResult.classList.contains('in-view')) {
			this.elStoreResult.classList.add('in-view');
			this.elStoreResult.classList.remove('is-hidden');
			this.elStoreMap.classList.add('is-hidden');
			this.elStoreMap.classList.remove('in-view');
			this.isList.classList.add('selected');
			this.isMap.classList.remove('selected');
		}
	}

	displayMap() {
		if (!this.elStoreMap.classList.contains('in-view')) {
			this.elStoreResult.classList.add('is-hidden');
			this.elStoreResult.classList.remove('in-view');
			this.elStoreMap.classList.add('in-view');
			this.elStoreMap.classList.remove('is-hidden');
			this.isMap.classList.add('selected');
			this.isList.classList.remove('selected');
			requestAnimationFrame(() => {
				google.maps.event.trigger(this.map, 'resize');
				google.maps.event.addListenerOnce(this.map, 'idle', () => {
					this.showVisibleMarkers(this.map, this.markers, this.listStores);
				});
			});
		}
	}
}
